<script setup lang="ts">
  import { ref, useTemplateRef, watch } from 'vue'

  const props = defineProps<{
    label?: string,
    aspectX: number,
    aspectY: number,
    imageCropperLabel: string,
    currentImage?: string,
    skipCrop?: boolean,
  }>()

  const emit = defineEmits<{
    (e: 'delete'): void,
  }>()

  const modelFile = defineModel<Blob | null>('image', { default: null })
  const crop = defineModel<CropData | null>('crop', { default: null })
  const preview = ref<string | null>(props.currentImage ?? null)

  const refImgCropper = useTemplateRef('refImgCropper')
  const files = ref<File[]>([])
  watch(files, () => {
    if (files.value[0]) {
      modelFile.value = files.value[0]

      if (props.skipCrop) {
        crop.value = {
          width: 0,
          height: 0,
          x: 0,
          y: 0,
        }
      } else {
        openImgCropper(files.value[0])
      }

      files.value = []
    }
  })

  const openImgCropper = (file: Blob) => {
    refImgCropper.value?.open(file)
  }

  const deleteImage = () => {
    crop.value = null
    preview.value = null
    modelFile.value = null
    emit('delete')
  }
</script>

<template>
  <app-input-file
    v-model="files"
    accept="image/*">
    <template #default="{ open }">
      <div class="group relative leading-0">
        <div class="absolute inset-0 border"></div>
        <app-button
          :style="{
            width: '100%',
            height: 'auto',
            aspectRatio: `${aspectX}/${aspectY}`,
          }"
          @click="open()">
          <app-image
            v-if="preview"
            :style="{
              width: '100%',
              height: 'auto',
              aspectRatio: `${aspectX}/${aspectY}`,
            }"
            :src="preview" />
          <!-- plus icon size depends to the container -->
          <div
            v-else
            class="size-full flex-center">
            <!-- create square at the center, plus size is 2/5 (40%) of the container size -->
            <div class="relative aspect-square h-full flex-center">
              <div class="absolute h-px w-2/9 bg-gray-200"></div>
              <div class="absolute h-2/9 w-px bg-gray-200"></div>
            </div>
          </div>
        </app-button>

        <!-- edit, delete, change image button -->
        <div
          v-if="currentImage || crop"
          class="absolute hidden group-hover:block pointer-events-none inset-0 bg-black/70"></div>
        <div class="absolute bottom-2 right-2 hidden items-center group-hover:flex space-x-1">
          <app-button
            v-if="currentImage"
            class="btn-low-priority btn-edit"
            title="Ganti Gambar"
            @click="open()">
            <fa-icon icon="i-far-folder-open" />
          </app-button>
          <app-button
            v-if="crop"
            class="btn-low-priority btn-edit"
            title="Crop Gambar"
            @click="openImgCropper(modelFile!)">
            <fa-icon icon="i-far-crop" />
          </app-button>
          <app-button
            v-if="currentImage || crop"
            class="btn-low-priority btn-edit"
            title="Hapus Gambar"
            @click="deleteImage()">
            <fa-icon icon="i-fas-xmark" />
          </app-button>
        </div>
      </div>
    </template>
  </app-input-file>

  {{ crop }}

  <app-image-cropper-2
    ref="refImgCropper"
    v-model:crop="crop"
    v-model:preview="preview"
    :title="imageCropperLabel"
    :aspect-x="aspectX"
    :aspect-y="aspectY" />
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .btn-edit {
    @apply bg-white size-6! p-0! text-xs! flex items-center justify-center rounded;
  }
</style>
